<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormUpload from '@/components/Atoms/FormUpload.vue'
import Alert from '@/components/Atoms/Alert.vue'
import { formatDurationShort } from './mediaflowMeta'

/**
 * Create or edit a break (ad pod, L-band): how long it runs, where Program
 * returns to and whether graphics stay up while it plays. Its layout is
 * edited on the Preview canvas like a scene.
 */
const props = defineProps({
	modelValue: { type: Boolean, default: false },
	/** Break flow being edited; null when creating. */
	flow: { type: Object, default: null },
	/** Layouts selectable as the return target. */
	layouts: { type: Array, default: () => [] },
	/** Live inputs available for the L-band template. */
	liveInputs: { type: Array, default: () => [] },
	/** Template preselected when creating (`'ad-pod'` | `'l-band'`); empty = the last one used. */
	initialTemplate: { type: String, default: '' },
	saving: { type: Boolean, default: false },
	error: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'save'])

const PREVIOUS = '__previous__'
const LAST_TEMPLATE_KEY = 'castr-mf-last-break-template'
const PRESETS = [15, 30, 60, 90, 120, 180]
const name = ref('')
const durationSec = ref(30)
const returnTo = ref(PREVIOUS)
const hideGraphics = ref(false)
const template = ref('blank')
const lBandInput = ref('')
const nameInput = ref(null)
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/avif']
const IMAGE_MAX_MB = 2
const imageFile = ref(null)
const imagePreview = ref('')
const imageError = ref('')
const imageInputId = `break-image-${Math.random().toString(36).slice(2, 8)}`

const clearImage = () => {
	imageFile.value = null
	imagePreview.value = ''
	imageError.value = ''
	const input = document.getElementById(imageInputId)
	if (input) input.value = ''
}
const onImagePicked = (file) => {
	imageError.value = ''
	if (!file) return
	if (!IMAGE_TYPES.includes(file.type)) {
		clearImage()
		imageError.value = 'Use a PNG, JPG, GIF or AVIF image.'
		return
	}
	if (file.size > IMAGE_MAX_MB * 1024 * 1024) {
		clearImage()
		imageError.value = `The image is over ${IMAGE_MAX_MB} MB.`
		return
	}
	imageFile.value = file
	const reader = new FileReader()
	reader.onload = () => { imagePreview.value = String(reader.result || '') }
	reader.readAsDataURL(file)
}

const open = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) })
const editing = computed(() => !!props.flow)

watch(() => [props.modelValue, props.flow], async ([visible]) => {
	if (!visible) return
	const b = props.flow?.break || {}
	name.value = props.flow?.name || ''
	durationSec.value = Number(b.durationSec) || 30
	returnTo.value = b.returnFlowId || PREVIOUS
	hideGraphics.value = b.overlays === 'hide'
	let wanted = props.initialTemplate
	if (!wanted) {
		try { wanted = localStorage.getItem(LAST_TEMPLATE_KEY) || 'ad-pod' } catch (e) { wanted = 'ad-pod' }
	}
	template.value = !props.flow && wanted === 'l-band' && props.liveInputs.length ? 'l-band' : 'blank'
	if (!props.flow) name.value = template.value === 'l-band' ? 'Sponsor L-band' : 'Ad pod'
	lBandInput.value = props.liveInputs[0]?.key || ''
	clearImage()
	await nextTick()
	nameInput.value?.focus?.()
}, { immediate: true })

const returnOptions = computed(() => [
	{ label: 'The scene that was on air before', value: PREVIOUS },
	...props.layouts.map((f) => ({ label: f.name, value: f._id })),
])
const inputOptions = computed(() => props.liveInputs.map((i) => ({ label: i.media.name, value: i.key })))
const valid = computed(() => (editing.value || !!name.value.trim()) && Number(durationSec.value) >= 1 && Number(durationSec.value) <= 7200)

const DEFAULT_NAMES = { blank: 'Ad pod', 'l-band': 'Sponsor L-band' }
const pickTemplate = (key) => {
	if (!editing.value && (!name.value.trim() || Object.values(DEFAULT_NAMES).includes(name.value))) name.value = DEFAULT_NAMES[key]
	template.value = key
}

const save = () => {
	if (!valid.value || props.saving) return
	if (!editing.value) {
		try { localStorage.setItem(LAST_TEMPLATE_KEY, template.value === 'l-band' ? 'l-band' : 'ad-pod') } catch (e) { /* private mode */ }
	}
	emit('save', {
		name: name.value.trim(),
		break: {
			durationSec: Math.round(Number(durationSec.value)),
			returnFlowId: returnTo.value === PREVIOUS ? null : returnTo.value,
			overlays: hideGraphics.value ? 'hide' : 'keep',
		},
		template: editing.value ? null : template.value,
		lBandInputKey: template.value === 'l-band' ? lBandInput.value : null,
		imageFile: editing.value ? null : imageFile.value,
	})
}
</script>

<template>
	<ModalBox v-model="open" :title="editing ? 'Break settings' : 'New break'" size="sm" placement="center" mobile-placement="bottom" header-class="!px-5 !pt-5 !pb-3">
		<form class="flex flex-col gap-3.5 px-5" @submit.prevent="save">
			<p class="m-0 text-sm text-surface-8">Takes over Program, then returns on its own.</p>
			<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>

			<FormInput v-if="!editing" ref="nameInput" v-model="name" label="Name" placeholder="e.g. Half-time ad pod, Sponsor L-band" :maxlength="120" data-testid="break-name" @keydown.enter.prevent="save" />

			<div v-if="!editing">
				<span class="mb-2 block text-tiny font-medium text-surface-text-1">Start from</span>
				<div class="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Template">
					<button type="button" role="radio" :aria-checked="template === 'blank'" class="rounded-lg px-3 py-2.5 text-left ring-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main" :class="template === 'blank' ? 'bg-surface-4 ring-main' : 'bg-surface-3 ring-transparent hover:ring-surface-6'" data-testid="break-template-blank" @click="pickTemplate('blank')">
						<span class="block text-tiny font-medium text-surface-text-2">Ad pod</span>
						<span class="block text-xs text-surface-8">Full-screen ad</span>
					</button>
					<button type="button" role="radio" :aria-checked="template === 'l-band'" class="rounded-lg px-3 py-2.5 text-left ring-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:opacity-50" :class="template === 'l-band' ? 'bg-surface-4 ring-main' : 'bg-surface-3 ring-transparent hover:ring-surface-6'" :disabled="!liveInputs.length" :title="!liveInputs.length ? 'Add a live camera to a scene first' : ''" data-testid="break-template-lband" @click="pickTemplate('l-band')">
						<span class="block text-tiny font-medium text-surface-text-2">L-band</span>
						<span class="block text-xs text-surface-8">Program squeezed top-right, frame image around it</span>
					</button>
				</div>
				<FormSelect v-if="template === 'l-band'" v-model="lBandInput" class="mt-2" label="Program input to squeeze" :options="inputOptions" data-testid="break-lband-input" />
			</div>

			<div v-if="!editing" data-testid="break-image">
				<span class="mb-2 block text-tiny font-medium text-surface-text-1">{{ template === 'l-band' ? 'Frame image' : 'Ad image' }} <span class="font-normal text-surface-8">(optional)</span></span>
				<div class="flex items-center gap-3">
					<img v-if="imagePreview" :src="imagePreview" alt="" class="h-12 w-20 shrink-0 bg-surface-3 object-contain" data-testid="break-image-preview" />
					<span v-else class="min-w-0 flex-1 text-xs text-surface-8">PNG, JPG, GIF or AVIF, up to {{ IMAGE_MAX_MB }} MB. You can also add it later in Sources.</span>
					<span v-if="imagePreview" class="min-w-0 flex-1 truncate text-xs text-surface-text-2" :title="imageFile?.name">{{ imageFile?.name }}</span>
					<FormUpload :id="imageInputId" name="break-image" type="secondary" size="sm" :label="imageFile ? 'Change' : 'Select image'" accept-type="image/png,image/jpeg,image/gif,image/avif" :disabled="saving" :on-file-change="onImagePicked" />
					<FormButton v-if="imageFile" type="secondary" size="sm" :disabled="saving" data-testid="break-image-remove" @click="clearImage">Remove</FormButton>
				</div>
				<p v-if="imageError" class="m-0 mt-1.5 text-xs text-error-light" data-testid="break-image-error">{{ imageError }}</p>
			</div>

			<div>
				<FormInput v-model="durationSec" type="number" min="1" max="7200" label="Runs for (seconds)" data-testid="break-duration" />
				<div class="mt-2 flex flex-wrap gap-1">
					<button v-for="s in PRESETS" :key="s" type="button" class="h-6 rounded px-2 font-mono text-[0.7rem] tabular-nums ring-1 ring-surface-4" :class="Number(durationSec) === s ? 'bg-surface-4 text-white' : 'text-surface-8 hover:text-white'" @click="durationSec = s">{{ formatDurationShort(s) }}</button>
				</div>
			</div>

			<FormSelect v-model="returnTo" label="Then return to" :options="returnOptions" data-testid="break-return" />

			<FormSwitch v-model="hideGraphics" size="md" label="Hide graphics while it runs" label-position="right" data-testid="break-hide-graphics" />
			<p class="-mt-2 m-0 text-xs text-surface-8">Logos, lower thirds and tickers go off air for the break and come back with the program.</p>
		</form>
		<template #footer>
			<div class="flex flex-col-reverse gap-2 px-5 pb-5 pt-4 sm:flex-row sm:justify-end">
				<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="saving" @click="open = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!valid" :is-loading="saving" data-testid="break-save" @click="save">{{ editing ? 'Save' : 'Create break' }}</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
